import { useCallback, useState } from 'react';
import { collection, query, getDocs, where, doc, deleteDoc, updateDoc, writeBatch } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { db } from '../../../config/firebaseConfig';
import { deleteVisitMirrors, isMirrorVisit } from '../../../utils/hybridVisitMirror';
import { normalizePatientId } from '../../../utils/patientAccess';
import { normalizeDate } from '../../ProfilePage/calendar/calendarDates';

/**
 * Operasyon Yönetimi işlemleri: vizit/iş açma ve güncelleme, iş silme, seri
 * silme ("bu ve sonrakiler", yalnızca admin) ve Detaylı Rapor (Excel).
 * İşlem kodu eski sayfadakiyle aynıdır; Excel başlıkları Türkçe kalır.
 */
const useOperationsActions = ({
  isAdmin,
  employees,
  getProjectName,
  currentDate,
  selectedEmployeeId,
  data,
  text,
}) => {
  const {
    setVisits,
    setCalendarJobs,
    fetchDataForMonth,
    setAllDataLoaded,
    allVisits,
    allCalendarJobs,
    allDataLoaded,
    fetchAllVisitsAndJobs,
  } = data;

  const [selectedVisit, setSelectedVisit] = useState(null);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDeleteModal, setShowJobDeleteModal] = useState(false);
  const [isDeletingJob, setIsDeletingJob] = useState(false);

  // Cascade delete ("this one and everything after it") — admin only.
  // `cascade` holds the anchor item plus the full list of documents that the
  // confirm step will remove, so the preview and the delete can never diverge.
  const [cascade, setCascade] = useState(null);
  const [isBuildingCascade, setIsBuildingCascade] = useState(false);
  const [isCascadeDeleting, setIsCascadeDeleting] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const handleVisitClick = (visit) => {
    setSelectedVisit(visit);
    setShowVisitModal(true);
  };

  const handleVisitUpdate = async (updatedVisit) => {
    // Check if the visit was deleted
    if (updatedVisit.deleted) {
      // Remove the deleted visit from local state
      setVisits((prevVisits) =>
        prevVisits.filter((visit) => visit.id !== updatedVisit.id)
      );
    } else {
      // Update local state immediately for better UX
      setVisits((prevVisits) =>
        prevVisits.map((visit) => (visit.id === updatedVisit.id ? updatedVisit : visit))
      );
    }
    // Refresh data from Firestore
    await fetchDataForMonth(currentDate, selectedEmployeeId);
    // Mark the full-employee cache stale so Personnel Reports refetches next open
    setAllDataLoaded(false);
  };

  const handleJobClick = (job) => {
    setSelectedJob(job);
    setShowJobDeleteModal(true);
  };

  const handleToggleJobStatus = async () => {
    if (!selectedJob?.id) return;

    try {
      const jobRef = doc(db, 'CalendarJobs', selectedJob.id);
      const newStatus = !selectedJob.status;
      await updateDoc(jobRef, {
        status: newStatus,
        lastUpdated: new Date().toISOString()
      });

      // Update local state
      const updatedJob = { ...selectedJob, status: newStatus };
      setCalendarJobs(prevJobs =>
        prevJobs.map(job => job.id === selectedJob.id ? { ...job, status: newStatus } : job)
      );
      setSelectedJob(updatedJob);
      setAllDataLoaded(false);

      toast.success(newStatus ? text.jobDone : text.jobOpen);
    } catch (error) {
      console.error('Error updating job status:', error);
      toast.error(text.jobUpdateError);
    }
  };

  const handleDeleteJob = async () => {
    if (!selectedJob?.id) return;

    setIsDeletingJob(true);
    try {
      const jobRef = doc(db, 'CalendarJobs', selectedJob.id);
      await deleteDoc(jobRef);

      // Remove the deleted job from local state
      setCalendarJobs((prevJobs) =>
        prevJobs.filter((job) => job.id !== selectedJob.id)
      );

      toast.success(text.jobDeleted);
      setShowJobDeleteModal(false);
      setSelectedJob(null);

      // Refresh data from Firestore
      await fetchDataForMonth(currentDate, selectedEmployeeId);
      setAllDataLoaded(false);
    } catch (error) {
      console.error('Error deleting job:', error);
      toast.error(text.jobDeleteError);
    } finally {
      setIsDeletingJob(false);
    }
  };

  /**
   * Cascade delete — "delete this item and everything that follows it".
   *
   * Series definition (admin decision):
   *   visits → same employeeId AND same patientId (Hasta ID)
   *   jobs   → same employeeId AND same title (jobs carry no patientId)
   *
   * The anchor item is always included. The set is read straight from
   * Firestore rather than from local state, so it stays complete even when the
   * calendar is showing a single month or a different employee filter.
   */
  const getVisitDate = useCallback((visit) => {
    const raw = visit.date || visit.firstVisitDate;
    if (!raw) return null;
    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? null : normalizeDate(parsed);
  }, []);

  const buildCascadePreview = useCallback(async (item, type) => {
    if (!isAdmin) return;
    if (!item?.employeeId) {
      toast.error(text.cascadeNoEmployee);
      return;
    }

    // A visit with no Hasta ID must never form a series: the old '-' sentinel
    // would otherwise group every unassigned visit of the employee together.
    const seriesKey = type === 'visit' ? normalizePatientId(item.patientId) : item.title;
    if (!seriesKey) {
      toast.error(type === 'visit' ? text.cascadeNoPatient : text.cascadeNoTitle);
      return;
    }

    const anchorDate = type === 'visit'
      ? getVisitDate(item)
      : (item.startDate ? normalizeDate(new Date(item.startDate)) : null);
    if (!anchorDate || isNaN(anchorDate.getTime())) {
      toast.error(text.cascadeNoDate);
      return;
    }

    setIsBuildingCascade(true);
    setCascade({ type, anchor: item, seriesKey, anchorDate, items: [] });

    try {
      const collectionName = type === 'visit' ? 'Visits' : 'CalendarJobs';
      const snapshot = await getDocs(query(
        collection(db, collectionName),
        where('employeeId', '==', item.employeeId)
      ));

      const matches = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((record) => {
          const key = type === 'visit' ? record.patientId : record.title;
          if (key !== seriesKey) return false;
          const recordDate = type === 'visit'
            ? getVisitDate(record)
            : (record.startDate ? normalizeDate(new Date(record.startDate)) : null);
          if (!recordDate || isNaN(recordDate.getTime())) return false;
          // Include the anchor itself and everything dated on/after it.
          return recordDate.getTime() >= anchorDate.getTime() || record.id === item.id;
        })
        .sort((a, b) => {
          const aDate = type === 'visit' ? getVisitDate(a) : normalizeDate(new Date(a.startDate));
          const bDate = type === 'visit' ? getVisitDate(b) : normalizeDate(new Date(b.startDate));
          return aDate - bDate;
        });

      setCascade({ type, anchor: item, seriesKey, anchorDate, items: matches });
    } catch (error) {
      console.error('Error building cascade preview:', error);
      toast.error(text.cascadeListError);
      setCascade(null);
    } finally {
      setIsBuildingCascade(false);
    }
    // text yalnızca dil değişince değişir
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, getVisitDate]);

  const handleConfirmCascadeDelete = async () => {
    if (!isAdmin || !cascade?.items?.length) return;

    setIsCascadeDeleting(true);
    try {
      const collectionName = cascade.type === 'visit' ? 'Visits' : 'CalendarJobs';
      // Firestore caps a batch at 500 writes.
      for (let i = 0; i < cascade.items.length; i += 450) {
        const batch = writeBatch(db);
        cascade.items.slice(i, i + 450).forEach((record) => {
          batch.delete(doc(db, collectionName, record.id));
        });
        await batch.commit();
      }

      // Hibrit proje: silinen vizitlerden türeyen tamamlanmamış eş vizitler de silinir
      if (cascade.type === 'visit') {
        try {
          await Promise.all(
            cascade.items
              .filter((record) => !isMirrorVisit(record))
              .map((record) => deleteVisitMirrors(record.id))
          );
        } catch (mirrorError) {
          console.error('Eş vizitler silinemedi:', mirrorError);
          toast.error(text.mirrorError);
        }
      }

      const deletedIds = new Set(cascade.items.map((record) => record.id));
      if (cascade.type === 'visit') {
        setVisits((prev) => prev.filter((v) => !deletedIds.has(v.id)));
      } else {
        setCalendarJobs((prev) => prev.filter((j) => !deletedIds.has(j.id)));
      }

      toast.success(text.cascadeDeleted(cascade.items.length));
      setCascade(null);
      setShowJobDeleteModal(false);
      setSelectedJob(null);
      setSelectedVisit(null);

      await fetchDataForMonth(currentDate, selectedEmployeeId);
      setAllDataLoaded(false);
    } catch (error) {
      console.error('Error during cascade delete:', error);
      toast.error(text.cascadeDeleteError);
    } finally {
      setIsCascadeDeleting(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      toast.error(text.reportDates);
      return;
    }

    try {
      // Ensure the full dataset is loaded (report spans all employees).
      let sourceVisits = allVisits;
      let sourceJobs = allCalendarJobs;
      if (!allDataLoaded) {
        const loaded = await fetchAllVisitsAndJobs();
        sourceVisits = loaded.visits;
        sourceJobs = loaded.jobs;
      }

      // Tarih aralığına göre visitleri filtrele
      const filteredVisits = sourceVisits.filter(visit => {
        const visitDate = new Date(visit.date || visit.firstVisitDate);
        const start = new Date(startDate);
        const end = new Date(endDate);
        return visitDate >= start && visitDate <= end;
      });

      // Tarih aralığına göre işleri filtrele
      const filteredJobs = sourceJobs.filter(job => {
        // İşler için startDate kullan (date field'ı yok)
        const jobStartDate = new Date(job.startDate);
        const start = new Date(startDate);
        const end = new Date(endDate);
        return jobStartDate >= start && jobStartDate <= end;
      });

      // Excel workbook oluştur
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      
      // Visits sayfası
      const visitsSheet = workbook.addWorksheet('Visitler');
      visitsSheet.columns = [
        { header: 'Tarih', key: 'date', width: 15 },
        { header: 'Çalışan', key: 'employeeName', width: 20 },
        { header: 'Müşteri', key: 'customerName', width: 25 },
        { header: 'Proje', key: 'projectName', width: 25 },
        { header: 'Açıklama', key: 'description', width: 30 },
        { header: 'Durum', key: 'status', width: 15 },
        { header: 'Başlangıç Saati', key: 'startTime', width: 15 },
        { header: 'Bitiş Saati', key: 'endTime', width: 15 },
        { header: 'Süre (Saat)', key: 'duration', width: 15 }
      ];

      // Visit verilerini ekle
      filteredVisits.forEach(visit => {
        // Çalışan bilgisini bul
        const employee = employees.find(emp => emp.id === visit.employeeId);
        const employeeName = employee ? `${employee.eName} ${employee.eSurname}` : visit.employeeId;

        const vStartTime = visit.visitStartTime || visit.startTime || '';
        const vEndTime = visit.visitEndTime || visit.endTime || '';

        // Süreyi hesapla (saat cinsinden)
        let duration = '';
        if (vStartTime && vEndTime) {
          const [sh, sm] = vStartTime.split(':').map(Number);
          const [eh, em] = vEndTime.split(':').map(Number);
          if (!isNaN(sh) && !isNaN(sm) && !isNaN(eh) && !isNaN(em)) {
            const diffMinutes = (eh * 60 + em) - (sh * 60 + sm);
            if (diffMinutes > 0) {
              duration = (diffMinutes / 60).toFixed(1);
            }
          }
        }

        visitsSheet.addRow({
          date: new Date(visit.date || visit.firstVisitDate).toLocaleDateString('tr-TR'),
          employeeName: employeeName,
          customerName: visit.patientId || '-',
          projectName: getProjectName(visit.projectId) || visit.projectId || '-',
          description: visit.info || '',
          status: visit.status ? 'Tamamlandı' : 'Devam Ediyor',
          startTime: vStartTime,
          endTime: vEndTime,
          duration: duration
        });
      });

      // İşler sayfası (sadece iş varsa oluştur)
      if (filteredJobs.length > 0) {
        const jobsSheet = workbook.addWorksheet('İşler');
        jobsSheet.columns = [
          { header: 'Tarih', key: 'date', width: 15 },
          { header: 'Çalışan', key: 'employeeName', width: 20 },
          { header: 'Müşteri', key: 'customerName', width: 25 },
          { header: 'Proje', key: 'projectName', width: 25 },
          { header: 'İş Türü', key: 'jobType', width: 20 },
          { header: 'Açıklama', key: 'description', width: 30 },
          { header: 'Durum', key: 'status', width: 15 },
          { header: 'Süre (Saat)', key: 'duration', width: 15 }
        ];

        // İş verilerini ekle
        filteredJobs.forEach(job => {
          // Çalışan bilgisini bul
          const employee = employees.find(emp => emp.id === job.employeeId);
          const employeeName = employee ? `${employee.eName} ${employee.eSurname}` : job.employeeId;
          
          jobsSheet.addRow({
            date: new Date(job.startDate).toLocaleDateString('tr-TR'),
            employeeName: employeeName,
            customerName: '',
            projectName: '',
            jobType: job.title || 'Genel',
            description: '',
            status: job.status ? 'Tamamlandı' : 'Devam Ediyor',
            duration: ''
          });
        });
      }

      // Excel dosyasını indir
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const fileName = `Detayli_Rapor_${startDate}_${endDate}.xlsx`;
      link.download = fileName;
      link.click();
      
      window.URL.revokeObjectURL(url);
      
      toast.success(text.reportDone(filteredVisits.length, filteredJobs.length));
      setShowDateRangePicker(false);
    } catch (error) {
      console.error('❌ Error generating report:', error);
      toast.error(text.reportError);
    }
  };

  return {
    selectedVisit,
    showVisitModal,
    setShowVisitModal,
    handleVisitClick,
    handleVisitUpdate,
    selectedJob,
    setSelectedJob,
    showJobDeleteModal,
    setShowJobDeleteModal,
    isDeletingJob,
    handleJobClick,
    handleToggleJobStatus,
    handleDeleteJob,
    cascade,
    setCascade,
    isBuildingCascade,
    isCascadeDeleting,
    buildCascadePreview,
    handleConfirmCascadeDelete,
    showDateRangePicker,
    setShowDateRangePicker,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    handleGenerateReport,
  };
};

export default useOperationsActions;
